<template>
  <div class="screen-page">
    <geo-map
      ref="geoMap"
      :services="mapServices"
      :counts="mapCounts"
      :active-id="activeType"
      @select="enterService"
      @enter="enterService"
    />
    <div class="hud-fx" />
    <div ref="stage" class="screen-stage map-pass">
      <header class="hud-hd">
        <div class="hd-left">
          <span class="hd-time">{{ clockDate }} {{ clockTime }}</span>
          <span class="hd-org">{{ currentOrder ? (currentOrder.name + ' · 在线督查') : '潜山乐康护理站 · 智慧养老服务平台' }}</span>
        </div>
        <div class="hd-center">
          <span class="wing left" />
          <h1 @click="goScreenHome">乐康智慧养老服务系统指挥中心</h1>
          <span class="wing right" />
        </div>
        <div class="hd-right">
          <button class="mode-btn" :class="{ on: viewMode === 'map' }" type="button" @click="switchMode('map')">地图模式</button>
          <button class="ghost-btn" type="button" @click="toggleFull">全屏</button>
          <button class="ghost-btn" type="button" @click="goHome">返回</button>
        </div>
      </header>

      <nav class="svc-tabs">
        <button
          type="button"
          class="svc-tab home-tab"
          :class="{ on: onHome }"
          @click="goScreenHome"
        >
          <i class="dot" style="background:#2ee6d6" />
          <span>指挥总览</span>
        </button>
        <button
          v-for="s in SERVICE_TYPES"
          :key="s.id"
          type="button"
          class="svc-tab"
          :class="{ on: !onHome && activeType === s.id }"
          @click="enterService(s.id)"
        >
          <i class="dot" :style="{ background: s.color }" />
          <span>{{ s.name }}</span>
          <b>{{ tabCount(s.id) }}</b>
        </button>
      </nav>

      <div class="hud-main" :class="{ 'is-home': onHome, 'is-board': !onHome, 'is-detail': !onHome && !!currentOrder }">
        <screen-home
          v-if="onHome"
          :kpis="homeKpis"
          :mix-chart="homeMixChart"
          :rank-chart="homeRankChart"
          :record-chart="homeRecordColChart"
          :user-chart="homeUserColChart"
          :order-chart="homeOrderChart"
          :user-rank-chart="homeUserRankChart"
          @select="enterService"
        />
        <template v-else>
        <aside class="hud-left tech-frame">
          <template v-if="!currentOrder">
            <div class="pane-title">
              <i class="el-icon-s-custom" />
              <span>被服务人</span>
            </div>
            <div class="filter-row">
              <input v-model="orderKeyword" class="hud-input" placeholder="姓名 / 订单号 / 地址">
            </div>
            <div class="order-pane">
              <div class="type-brief">
                <p>{{ activeMeta.desc }}</p>
                <div class="tag-row">
                  <span v-for="t in activeMeta.tags" :key="t">{{ t }}</span>
                </div>
              </div>
              <div class="order-head">共 {{ filteredOrders.length }} 人，点选进入详情</div>
              <ul class="order-list">
                <li v-if="orderLoading" class="empty">正在加载…</li>
                <li
                  v-for="o in filteredOrders"
                  :key="o.id"
                  @click="selectOrder(o)"
                >
                  <div class="od-top">
                    <strong>{{ o.name }}</strong>
                    <em :class="o.tone">{{ o.statusText }}</em>
                  </div>
                  <div class="od-sub">{{ o.no }}</div>
                  <div class="od-sub">{{ o.address || '—' }} · {{ o.nurse || '未指派' }}</div>
                </li>
                <li v-if="!filteredOrders.length && !orderLoading" class="empty">暂无被服务人</li>
              </ul>
            </div>
          </template>
          <template v-else>
            <div class="pane-title">
              <span>基本信息</span>
              <button class="close-x" type="button" @click="backPerson">返回</button>
            </div>
            <div class="person-info" v-loading="detailLoading">
              <ul class="kv">
                <li><span>姓名</span><b>{{ detail.user.name || currentOrder.name }}</b></li>
                <li><span>状态</span><b :class="detail.tone">{{ detail.status }}</b></li>
                <li><span>电话</span><b>{{ detail.user.tel || currentOrder.tel || '—' }}</b></li>
                <li><span>地址</span><b>{{ detail.user.address || currentOrder.address || '—' }}</b></li>
                <li v-if="detail.user.extra"><span>照护等级</span><b>{{ detail.user.extra }}</b></li>
                <li><span>订单号</span><b>{{ detail.no || currentOrder.no }}</b></li>
                <li><span>责任护工</span><b>{{ detail.nurse.name || currentOrder.nurse || '未指派' }}</b></li>
                <li><span>本月服务</span><b>{{ detail.monthNum || detail.records.length }} 次</b></li>
              </ul>
              <div class="quick-row">
                <span v-for="t in activeMeta.tags" :key="t">{{ t }}</span>
              </div>
              <div class="stat-hd">月度服务次数分析</div>
              <div class="mini-chart">
                <screen-chart :options="monthChart" />
              </div>
              <div class="stat-hd">年度服务次数分析</div>
              <div class="mini-chart">
                <screen-chart :options="yearChart" />
              </div>
            </div>
          </template>
        </aside>

        <section class="hud-center">
          <template v-if="currentOrder">
            <div class="detail-center">
              <div class="dc-hd">
                <span>{{ detail.user.name || currentOrder.name }} · 服务影像</span>
                <div class="dc-modes">
                  <button type="button" :class="{ on: photoMode === 'multi' }" @click="photoMode = 'multi'">多屏模式</button>
                  <button type="button" :class="{ on: photoMode === 'single' }" @click="photoMode = 'single'">单屏模式</button>
                </div>
              </div>
              <div v-if="photoMode === 'single' && displayPhotos.length" class="photo-single">
                <el-image :src="displayPhotos[0]" :preview-src-list="displayPhotos" fit="cover" />
              </div>
              <div v-else class="photo-grid" :class="{ single: photoMode === 'single' }">
                <div
                  v-for="(cell, i) in photoCells"
                  :key="i"
                  class="photo-cell"
                >
                  <el-image v-if="cell.url" :src="cell.url" :preview-src-list="displayPhotos" fit="cover" />
                  <div v-else class="photo-placeholder">
                    <i class="el-icon-picture-outline" />
                    <span>{{ cell.label }}</span>
                  </div>
                  <em>{{ cell.label }}</em>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="overview-center">
            <div class="ov-hd">
              <span>{{ activeMeta.name }} · 多维运行总览</span>
              <em class="live"><i class="pulse-dot" />实时监测</em>
            </div>
            <div class="ov-kpis dense">
              <div v-for="k in overviewKpis" :key="k.label" class="ov-kpi">
                <em>{{ k.label }}</em>
                <b>{{ k.value }}<small v-if="k.unit">{{ k.unit }}</small></b>
                <span>{{ k.hint }}</span>
              </div>
            </div>
            <div class="ov-grid rich">
              <div class="ov-panel">
                <div class="stat-hd">综合满意度 / 服务率</div>
                <div class="gauge-row">
                  <div class="gauge-box">
                    <screen-chart :options="overviewGauge" />
                    <p>满意度</p>
                  </div>
                  <div class="gauge-box">
                    <screen-chart :options="completeGauge" />
                    <p>服务率</p>
                  </div>
                </div>
              </div>
              <div class="ov-panel">
                <div class="stat-hd">订单状态结构</div>
                <screen-chart :options="statusPieChart" />
              </div>
              <div class="ov-panel">
                <div class="stat-hd">{{ extraPieTitle }}</div>
                <screen-chart :options="extraPieChart" />
              </div>
              <div class="ov-panel">
                <div class="stat-hd">年度同比</div>
                <screen-chart :options="yearCompareChart" />
              </div>
              <div class="ov-panel">
                <div class="stat-hd">近七日服务</div>
                <screen-chart :options="overviewTrend" />
              </div>
              <div class="ov-panel">
                <div class="stat-hd">综合能力雷达</div>
                <screen-chart :options="overviewRadar" />
              </div>
              <div class="ov-panel area-panel">
                <div class="stat-hd">覆盖区域<em>{{ areaList.length }} 个乡镇</em></div>
                <ul class="area-list">
                  <li v-for="a in areaList" :key="a.name">
                    <span class="area-name">{{ a.name }}</span>
                    <i class="area-track"><b :style="{ width: a.pct + '%' }" /></i>
                    <em class="area-num">{{ a.value }}</em>
                  </li>
                  <li v-if="!areaList.length" class="area-empty">暂无乡镇数据</li>
                </ul>
              </div>
              <div class="ov-panel area-panel">
                <div class="stat-hd">护工服务量<em>{{ nurseList.length }} 人</em></div>
                <ul class="area-list">
                  <li v-for="n in nurseList" :key="n.name">
                    <span class="area-name">{{ n.name }}</span>
                    <i class="area-track"><b :style="{ width: n.pct + '%' }" /></i>
                    <em class="area-num">{{ n.value }}</em>
                  </li>
                  <li v-if="!nurseList.length" class="area-empty">暂无护工数据</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <aside v-if="!currentOrder" class="hud-right tech-frame overview-side">
          <div class="pane-title">
            <span>实时服务动态</span>
            <em class="live mini"><i class="pulse-dot" />在线</em>
          </div>
          <div class="feed-viewport">
            <div
              class="feed-track"
              :class="{ rolling: overviewFeed.length > 0 }"
              :style="{ animationDuration: feedRollSec + 's' }"
            >
              <ul
                v-for="copy in feedCopies"
                :key="'feed-' + copy"
                class="feed-list"
              >
                <li v-for="(f, i) in overviewFeed" :key="copy + '-' + (f.id || i)">
                  <i :class="f.tone" />
                  <div>
                    <b>{{ f.t }} · {{ f.status }}</b>
                    <span>{{ f.text }}</span>
                    <em v-if="f.sub">{{ f.sub }}</em>
                  </div>
                </li>
              </ul>
            </div>
            <div v-if="feedLoading && !overviewFeed.length" class="empty">正在加载…</div>
            <div v-else-if="!overviewFeed.length" class="empty">暂无服务动态</div>
          </div>
        </aside>

        <aside v-else class="hud-right tech-frame">
          <div class="pane-title">
            <span>服务记录</span>
          </div>
          <div class="acc" v-loading="detailLoading">
            <div class="acc-block open">
              <div class="acc-hd">服务记录信息</div>
              <div v-for="(r, i) in detail.records" :key="r.id" class="rec-card">
                <div class="rec-top">
                  <em>{{ i + 1 }}</em>
                  <div>
                    <div class="rec-time">{{ (r.items && r.items[0]) || '服务记录' }} · {{ r.attendance || '已完成' }}</div>
                    <div class="rec-items">{{ r.time || '—' }} · {{ r.nurse || '—' }}</div>
                    <div v-if="r.note" class="rec-note">{{ r.note }}</div>
                    <div v-if="r.items && r.items.length > 1" class="rec-items">{{ r.items.join('、') }}</div>
                  </div>
                </div>
                <div v-if="r.images.length" class="rec-imgs">
                  <el-image
                    v-for="(u, j) in r.images"
                    :key="j"
                    :src="u"
                    :preview-src-list="r.images"
                    fit="cover"
                  />
                </div>
              </div>
              <div v-if="!detail.records.length" class="empty">暂无服务记录</div>
            </div>
            <div class="acc-block open">
              <div class="acc-hd">服务项目</div>
              <ul class="check-list">
                <li v-for="(it, i) in detail.allItems" :key="i">
                  <i class="el-icon-success" />
                  <span>{{ it }}</span>
                  <em>已落实</em>
                </li>
                <li v-if="!detail.allItems.length" class="empty">暂无服务项目</li>
              </ul>
            </div>
          </div>
        </aside>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import GeoMap from './components/GeoMap.vue'
import ScreenChart from './components/ScreenChart.vue'
import ScreenHome from './components/ScreenHome.vue'
import { lineOption, barOption, gaugeOption, countBarOption, radarOption, pieOption, vBarOption, colBarOption } from './chart-options'
import {
  SERVICE_TYPES,
  loadAllCounts,
  loadAllServiceOrders,
  loadServiceList,
  loadOrderDetail,
  buildServiceOverview,
  statusText,
  statusTone
} from './catalog'
import {
  fetchDashboardSummary,
  fetchDashboardRecord,
  fetchDashboardTfStat,
  fetchFosterRecordList,
  fetchStatisticsOrderQuantity,
  fetchStatisticsOrderYmw
} from '@/api'

export default {
  name: 'SmartElderlyScreen',
  components: { GeoMap, ScreenChart, ScreenHome },
  data() {
    return {
      SERVICE_TYPES,
      clockDate: '',
      clockTime: '',
      timer: null,
      viewMode: 'map',
      orderKeyword: '',
      hoverId: '',
      popupId: '',
      activeType: '',
      counts: {},
      orders: [],
      orderLoading: false,
      currentOrder: null,
      detail: emptyDetail(),
      detailLoading: false,
      photoMode: 'multi',
      platformStats: { week: 0, month: 0, year: 0, total: 0 },
      ymw: null,
      dashboardReady: false,
      dashboard: emptyDashboard(),
      serviceFeed: [],
      feedLoading: false,
      feedSeq: 0,
      feedType: '',
      visitPie: [],
      visitSeq: 0
    }
  },
  computed: {
    activeMeta() {
      return this.SERVICE_TYPES.filter(s => s.id === this.activeType)[0] || this.SERVICE_TYPES[0]
    },
    onHome() {
      return !this.activeType
    },
    filteredOrders() {
      const k = (this.orderKeyword || '').trim()
      if (!k) return this.orders
      return this.orders.filter(o => {
        return (o.name && o.name.indexOf(k) > -1) ||
          (o.no && String(o.no).indexOf(k) > -1) ||
          (o.address && o.address.indexOf(k) > -1)
      })
    },
    overview() {
      const c = this.counts[this.activeType] || {}
      return buildServiceOverview(
        this.activeType,
        this.countOf(this.activeType) || c.total,
        c.served,
        this.orders
      )
    },
    overviewKpis() {
      const o = this.overview
      const p = this.platformStats
      return [
        { label: '在册对象', value: o.people, unit: '人', hint: '本服务建档人数' },
        { label: '本月服务', value: o.monthTimes, unit: '次', hint: '按计划落实' },
        { label: '服务中', value: (o.running && o.running.run) || 0, unit: '人', hint: '实时在护' },
        { label: '已完成', value: (o.running && o.running.done) || 0, unit: '人', hint: '闭环归档' },
        { label: '在岗护工', value: o.onDuty, unit: '人', hint: '智能排班' },
        { label: '满意度', value: o.satisfaction, unit: '%', hint: '家属评价' },
        { label: '近7天', value: p.week || o.todayTimes, unit: '单', hint: '平台订单' },
        { label: '累计', value: p.total || o.people, unit: '单', hint: '历史总量' }
      ]
    },
    mapServices() {
      return this.SERVICE_TYPES.filter(s => s.id !== 'nursing' && s.short !== '院内照护')
    },
    homeKpis() {
      const d = this.dashboard
      return [
        { label: '累计服务记录', value: toNum(d.total_record_num), unit: '次', hint: '全部服务记录' },
        { label: '累计服务人数', value: toNum(d.total_served_user_num), unit: '人', hint: '累计服务对象' },
        { label: '本年服务记录', value: toNum(d.year_record_num), unit: '次', hint: '本年度记录' },
        { label: '本年服务人数', value: toNum(d.year_served_user_num), unit: '人', hint: '本年度服务对象' },
        { label: '有效订单', value: this.homeOrderTotal, unit: '单', hint: '六类在服订单' }
      ]
    },
    homeServiceRows() {
      const list = Array.isArray(this.dashboard.record_list) ? this.dashboard.record_list : []
      const recordById = {}
      list.forEach(row => {
        let svc = matchService(String((row && row.name) || '').trim())
        if (!svc) return
        if (svc.id === 'nursing') svc = this.SERVICE_TYPES.filter(s => s.id === 'hospital')[0]
        if (!svc || svc.id === 'nursing') return
        const prev = recordById[svc.id] || { records: 0, users: 0 }
        recordById[svc.id] = {
          records: prev.records + toNum(row && row.record_num),
          users: prev.users + toNum(row && row.user_num)
        }
      })
      const orderById = {}
      SUMMARY_ORDER_FIELDS.forEach(f => {
        orderById[f.id] = toNum(this.dashboard[f.key])
      })
      return this.SERVICE_TYPES.map(s => {
        const rec = s.id === 'nursing' ? { records: 0, users: 0 } : (recordById[s.id] || { records: 0, users: 0 })
        return {
          id: s.id,
          name: s.name,
          color: s.color,
          records: rec.records,
          users: rec.users,
          orders: s.id === 'nursing' ? 0 : (orderById[s.id] || 0)
        }
      })
    },
    homeOrderTotal() {
      return SUMMARY_ORDER_FIELDS.reduce((sum, f) => sum + toNum(this.dashboard[f.key]), 0)
    },
    mapCounts() {
      if (!this.dashboardReady) return this.counts
      const next = Object.assign({}, this.counts)
      SUMMARY_ORDER_FIELDS.forEach(f => {
        const row = this.homeServiceRows.find(r => r.id === f.id)
        next[f.id] = {
          total: toNum(this.dashboard[f.key]),
          served: row ? row.records : 0,
          users: row ? row.users : 0
        }
      })
      return next
    },
    homeMixChart() {
      const rows = this.homeServiceRows
      const total = rows.reduce((sum, i) => sum + i.records, 0)
      const opt = pieOption(rows.map(i => ({
        name: i.name,
        value: i.records,
        itemStyle: { color: i.color }
      })), '次')
      opt.legend = {
        show: true,
        type: 'plain',
        orient: 'horizontal',
        left: 0,
        right: 0,
        bottom: 0,
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 8,
        textStyle: { color: 'rgba(214, 236, 247, 0.92)', fontSize: 11, overflow: 'none' },
        data: rows.map(i => i.name),
        formatter(name) {
          const row = rows.filter(i => i.name === name)[0]
          const pct = !row || !total ? '0' : (Math.round(row.records / total * 1000) / 10)
          return name + ' ' + pct + '%'
        }
      }
      opt.series[0].left = 28
      opt.series[0].right = 28
      opt.series[0].top = 8
      opt.series[0].bottom = 92
      opt.series[0].radius = ['34%', '58%']
      opt.series[0].center = ['50%', '50%']
      opt.series[0].minShowLabelAngle = 0
      opt.series[0].label = {
        show: true,
        position: 'outside',
        formatter: params => (Math.round(params.percent * 10) / 10) + '%',
        color: 'rgba(232, 251, 255, 0.95)',
        fontSize: 11,
        overflow: 'none'
      }
      opt.series[0].labelLine = {
        show: true,
        length: 8,
        length2: 6,
        lineStyle: { color: 'rgba(160, 200, 220, 0.45)' }
      }
      opt.series[0].labelLayout = { hideOverlap: false }
      return opt
    },
    homeRankChart() {
      const rows = this.homeServiceRows.slice().sort((a, b) => b.records - a.records)
      return countBarOption(
        rows.map(i => i.name),
        rows.map(i => i.records),
        rows.map(i => i.color)
      )
    },
    homeUserRankChart() {
      const rows = this.homeServiceRows.slice().sort((a, b) => b.users - a.users)
      return countBarOption(
        rows.map(i => i.name),
        rows.map(i => i.users),
        rows.map(i => i.color)
      )
    },
    homeOrderChart() {
      const rows = this.homeServiceRows
      return countBarOption(
        rows.map(i => i.name),
        rows.map(i => i.orders),
        rows.map(i => i.color)
      )
    },
    homeRecordColChart() {
      const rows = this.homeServiceRows
      return colBarOption(rows.map(i => i.name), rows.map(i => i.records), rows.map(i => i.color))
    },
    homeUserColChart() {
      const rows = this.homeServiceRows
      return colBarOption(rows.map(i => i.name), rows.map(i => i.users), rows.map(i => i.color))
    },
    extraPieTitle() {
      const map = {
        sn: '失能等级',
        boarding: '托养类型',
        nursing: '能力等级',
        hospital: '医院分布',
        tf: '探访方式',
        tk: '护理类型',
        foster: '服务类型'
      }
      return map[this.activeType] || '服务结构'
    },
    statusPieChart() {
      return pieOption(colorizePie(this.overview.statusPie, ['#f0c36a', '#2ee6d6', '#7ee08a', '#8899a4', '#4fc3f7']))
    },
    extraPieChart() {
      const rows = this.activeType === 'tf' ? this.visitPie : this.overview.extraPie
      const unit = this.activeType === 'tf' ? '次' : '人'
      return pieOption(colorizePie(rows, ['#2ee6d6', '#4fc3f7', '#c792ea', '#ff9f43', '#7ee08a', '#f0c36a']), unit)
    },
    areaList() {
      const rows = (this.overview.areaRank || []).filter(i => /(?:镇|乡)$/.test(i.name))
      const max = rows.reduce((m, i) => Math.max(m, Number(i.value) || 0), 0) || 1
      return rows.map(i => ({
        name: i.name,
        value: i.value,
        pct: Math.max(6, Math.round((Number(i.value) || 0) / max * 100))
      }))
    },
    nurseList() {
      const rows = (this.overview.nurseRank || [])
        .filter(i => i.name && i.name !== '未指派')
        .slice(0, 40)
      const max = rows.reduce((m, i) => Math.max(m, Number(i.value) || 0), 0) || 1
      return rows.map(i => ({
        name: i.name,
        value: i.value,
        pct: Math.max(6, Math.round((Number(i.value) || 0) / max * 100))
      }))
    },
    yearCompareChart() {
      const list = (this.ymw && this.ymw.list) || {}
      const keys = Object.keys(list)
      if (!keys.length) {
        return vBarOption(this.overview.trendX, this.overview.trendY, this.overview.trendY.map(v => Math.max(0, v - 3)))
      }
      return vBarOption(
        keys,
        keys.map(k => Number((list[k] && list[k].current) || 0)),
        keys.map(k => Number((list[k] && list[k].previous) || 0))
      )
    },
    overviewRadar() {
      const o = this.overview
      return radarOption(
        [
          { name: '及时率', max: 100 },
          { name: '规范率', max: 100 },
          { name: '满意度', max: 100 },
          { name: '服务率', max: 100 },
          { name: '覆盖率', max: 100 },
          { name: '安全', max: 100 }
        ],
        [o.onTime, 99, o.satisfaction, o.complete, 96, 100]
      )
    },
    completeGauge() {
      return gaugeOption(Math.round(this.overview.complete))
    },
    overviewHighlights() {
      return [
        '国家级规范落实',
        '智能巡检在线',
        '家属好评 ' + this.overview.praise,
        '准时率 ' + this.overview.onTime + '%',
        '服务率 ' + this.overview.complete + '%'
      ]
    },
    overviewTrend() {
      const list = this.ymw && this.ymw.list
      if (list && Object.keys(list).length) {
        const keys = Object.keys(list)
        return lineOption(keys, [
          {
            name: '本期',
            data: keys.map(k => Number((list[k] && list[k].current) || 0)),
            itemStyle: { color: '#2ee6d6' },
            lineStyle: { color: '#2ee6d6', width: 2 },
            areaStyle: { color: 'rgba(46,230,214,0.16)' }
          },
          {
            name: '同期',
            data: keys.map(k => Number((list[k] && list[k].previous) || 0)),
            itemStyle: { color: '#4fc3f7' },
            lineStyle: { color: '#4fc3f7', width: 2 }
          }
        ])
      }
      const o = this.overview
      return lineOption(o.trendX || [], [
        {
          name: '服务量',
          data: o.trendY || [],
          itemStyle: { color: '#2ee6d6' },
          lineStyle: { color: '#2ee6d6', width: 2 },
          areaStyle: { color: 'rgba(46,230,214,0.16)' }
        }
      ])
    },
    overviewGauge() {
      return gaugeOption(Math.round(this.overview.satisfaction))
    },
    overviewBars() {
      const q = this.overview.quality || []
      return barOption(
        q.map(i => i.name),
        q.map(i => i.value),
        ['#2ee6d6', '#4fc3f7', '#7ee08a', '#f0c36a']
      )
    },
    overviewFeed() {
      return this.serviceFeed
    },
    feedCopies() {
      return this.overviewFeed.length ? 2 : 1
    },
    feedRollSec() {
      return Math.max(16, this.overviewFeed.length * 2.4)
    },
    displayPhotos() {
      return (this.detail && this.detail.photos) || []
    },
    monthChart() {
      const s = (this.detail && this.detail.stats) || {}
      return lineOption(s.monthX || [], [
        { name: '本月', data: s.monthCur || [], itemStyle: { color: '#2ee6d6' }, lineStyle: { color: '#2ee6d6', width: 2 }, areaStyle: { color: 'rgba(46,230,214,0.12)' } },
        { name: '上月', data: s.monthPrev || [], itemStyle: { color: '#f0c36a' }, lineStyle: { color: '#f0c36a', width: 2 } }
      ])
    },
    yearChart() {
      const s = (this.detail && this.detail.stats) || {}
      return lineOption(s.yearX || [], [
        { name: '本年', data: s.yearCur || [], itemStyle: { color: '#2ee6d6' }, lineStyle: { color: '#2ee6d6', width: 2 }, areaStyle: { color: 'rgba(46,230,214,0.12)' } },
        { name: '去年', data: s.yearPrev || [], itemStyle: { color: '#4fc3f7' }, lineStyle: { color: '#4fc3f7', width: 2 } }
      ])
    },
    photoCells() {
      const records = (this.detail && this.detail.records) || []
      const cells = []
      records.forEach(r => {
        const label = (r.items && r.items[0]) || '服务记录'
        if (r.images && r.images.length) {
          r.images.forEach(u => cells.push({ url: u, label }))
        } else {
          cells.push({ url: '', label })
        }
      })
      if (!cells.length && this.detail.allItems) {
        this.detail.allItems.forEach(n => cells.push({ url: '', label: n }))
      }
      while (cells.length < 6) cells.push({ url: '', label: '服务影像' })
      return this.photoMode === 'single' ? cells.slice(0, 1) : cells.slice(0, 6)
    }
  },
  mounted() {
    document.body.style.overflow = 'hidden'
    this.tickClock()
    this.timer = setInterval(this.tickClock, 1000)
    this.setScale()
    window.addEventListener('resize', this.setScale)
    this.refreshCounts()
    this.loadPlatformStats()
    this.loadDashboard()
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    clearInterval(this.timer)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    tickClock() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      this.clockDate = `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`
      this.clockTime = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },
    setScale() {
      const el = this.$refs.stage
      if (!el) return
      const scale = Math.min(window.innerWidth / 1920, window.innerHeight / 1080)
      el.style.transform = `scale(${scale})`
      el.style.left = `${(window.innerWidth - 1920 * scale) / 2}px`
      el.style.top = `${(window.innerHeight - 1080 * scale) / 2}px`
      this.$nextTick(() => this.resizeMap())
    },
    resizeMap() {
      const m = this.$refs.geoMap
      if (m && m.resize) m.resize()
    },
    toggleFull() {
      this.Utils.fullScreen()
    },
    goHome() {
      this.$router.push('/home').catch(() => {})
    },
    goScreenHome() {
      this.activeType = ''
      this.hoverId = ''
      this.popupId = ''
      this.currentOrder = null
      this.detail = emptyDetail()
      this.orders = []
      this.orderKeyword = ''
      this.serviceFeed = []
      this.feedLoading = false
      this.feedType = ''
      this.visitSeq += 1
      this.visitPie = []
      this.$nextTick(() => this.resizeMap())
    },
    countOf(id) {
      return (this.counts[id] && this.counts[id].total) || 0
    },
    summaryOrderOf(id) {
      if (!this.dashboardReady) return null
      const field = SUMMARY_ORDER_FIELDS.find(f => f.id === id)
      if (!field) return null
      return toNum(this.dashboard[field.key])
    },
    tabCount(id) {
      const n = this.summaryOrderOf(id)
      return n == null ? this.countOf(id) : n
    },
    loadDashboard() {
      fetchDashboardSummary({}, { silent: true }).then(res => {
        if (res && res.status && res.data) {
          this.dashboard = Object.assign(emptyDashboard(), res.data)
          this.dashboardReady = true
        }
      }).catch(() => {})
    },
    refreshCounts() {
      loadAllCounts().then(res => {
        this.counts = res || {}
      })
    },
    loadPlatformStats() {
      const year = String(new Date().getFullYear())
      const quiet = { silent: true }
      fetchStatisticsOrderQuantity(undefined, quiet).then(res => {
        if (res && res.data) {
          this.platformStats = {
            week: res.data.last_seven_days,
            month: res.data.last_thirty_days,
            year: res.data.last_year_days,
            total: res.data.order_total
          }
        }
      }).catch(() => {})
      fetchStatisticsOrderYmw({ type: 'year', date: year }, quiet).then(res => {
        if (res && res.data) this.ymw = res.data
      }).catch(() => {})
    },
    switchMode(mode) {
      this.viewMode = mode
      if (mode === 'map') this.backPerson()
      this.$nextTick(() => this.resizeMap())
    },
    backPerson() {
      this.currentOrder = null
      this.detail = emptyDetail()
      this.$nextTick(() => this.resizeMap())
    },
    enterService(id) {
      if (!id) return
      const same = this.activeType === id && this.orders.length
      this.activeType = id
      this.hoverId = id
      this.popupId = id
      this.currentOrder = null
      this.detail = emptyDetail()
      this.loadServiceFeed(id)
      if (!(same && id === 'tf' && this.visitPie.length)) this.loadVisitMethods(id)
      if (same) {
        this.$nextTick(() => this.resizeMap())
        return
      }
      this.orderKeyword = ''
      this.orders = []
      this.orderLoading = true
      loadAllServiceOrders(id, 500).then(res => {
        this.orders = (res && res.list) || []
        this.$set(this.counts, id, { total: res.total, served: res.served })
        this.orderLoading = false
      }).catch(() => {
        this.orderLoading = false
      })
    },
    loadServiceFeed(typeId) {
      const serviceId = DASHBOARD_SERVICE_ID[typeId]
      if (this.feedType !== typeId) {
        this.serviceFeed = []
        this.feedType = typeId
      }
      const seq = this.feedSeq + 1
      this.feedSeq = seq
      this.feedLoading = true
      if (typeId === 'hospital') {
        this.collectHospitalFeed(seq)
        return
      }
      if (typeId === 'foster') {
        this.collectFosterFeed(seq)
        return
      }
      if (serviceId == null) {
        this.feedLoading = false
        return
      }
      this.collectServiceRecords(String(serviceId), seq)
    },
    collectFosterFeed(seq) {
      const rows = []
      const step = page => {
        if (this.feedSeq !== seq || rows.length >= FEED_LIMIT || page > 20) {
          if (this.feedSeq === seq) {
            this.serviceFeed = rows.slice(0, FEED_LIMIT).map(row => feedFromRecord(row, '12'))
            this.feedLoading = false
          }
          return
        }
        fetchFosterRecordList({
          page_size: 100,
          page_number: page,
          service_item: '',
          order: '',
          user_name_or_tel: '',
          nurse_name_or_tel: '',
          order_status: ''
        }, { silent: true }).then(res => {
          if (this.feedSeq !== seq) return
          const data = (res && res.data) || {}
          const list = Array.isArray(data.list) ? data.list : []
          list.forEach(row => rows.push(fosterFeedRow(row)))
          this.serviceFeed = rows.slice(0, FEED_LIMIT).map(item => feedFromRecord(item, '12'))
          if (!list.length || list.length < 100 || rows.length >= FEED_LIMIT) {
            this.feedLoading = false
            return
          }
          step(page + 1)
        }).catch(() => {
          if (this.feedSeq === seq) this.feedLoading = false
        })
      }
      step(1)
    },
    collectHospitalFeed(seq) {
      const rows = []
      const seen = {}
      const step = page => {
        if (this.feedSeq !== seq || rows.length >= FEED_LIMIT || page > 20) {
          if (this.feedSeq === seq) this.feedLoading = false
          return
        }
        loadServiceList('hospital', 50, null, page).then(res => {
          if (this.feedSeq !== seq) return
          const list = (res && res.list) || []
          list.forEach(order => {
            const id = order && order.id
            if (id != null && id !== '' && seen[id]) return
            if (id != null && id !== '') seen[id] = true
            rows.push({
              id: order.id,
              name: order.name,
              created_at: order.lastAt,
              order_status: order.status,
              nurse_name: order.nurse,
              address: order.address,
              service_names: ['医院陪护']
            })
          })
          this.serviceFeed = rows.slice(0, FEED_LIMIT).map(feedFromRecord)
          if (!list.length || list.length < 50 || rows.length >= FEED_LIMIT) {
            this.feedLoading = false
            return
          }
          step(page + 1)
        }).catch(() => {
          if (this.feedSeq === seq) this.feedLoading = false
        })
      }
      step(1)
    },
    loadVisitMethods(typeId) {
      const seq = this.visitSeq + 1
      this.visitSeq = seq
      this.visitPie = []
      if (typeId !== 'tf') return
      fetchDashboardTfStat({}, { silent: true }).then(res => {
        if (this.visitSeq !== seq) return
        const list = Array.isArray(res && res.data) ? res.data : []
        this.visitPie = list
          .filter(row => row && row.name)
          .map(row => ({
            name: String(row.name),
            value: Number(row.record_num) || 0
          }))
      }).catch(() => {})
    },
    collectServiceRecords(serviceId, seq) {
      const rows = []
      const seen = {}
      const apply = () => {
        if (this.feedSeq !== seq) return
        this.serviceFeed = rows.slice(0, FEED_LIMIT).map(row => feedFromRecord(row, serviceId))
      }
      const finish = () => {
        if (this.feedSeq === seq) this.feedLoading = false
      }
      const pull = (page, size) => {
        const data = {
          page_number: String(page),
          service_id: serviceId
        }
        if (size) data.page_size = String(size)
        return fetchDashboardRecord(data, { silent: true })
      }
      const step = (page, size) => {
        if (this.feedSeq !== seq || rows.length >= FEED_LIMIT || page > FEED_LIMIT) {
          finish()
          return
        }
        pull(page, size).then(res => {
          if (this.feedSeq !== seq) return
          const packed = unpackRecordPage(res)
          if (page === 1 && size && !packed.list.length) {
            step(1, 0)
            return
          }
          const before = rows.length
          pushRecords(rows, seen, packed.list)
          apply()
          const pageSize = packed.pageSize || packed.list.length
          const total = packed.total || rows.length
          const noMore = !packed.list.length ||
            rows.length === before ||
            rows.length >= FEED_LIMIT ||
            (total > 0 && rows.length >= total) ||
            (pageSize > 0 && packed.list.length < pageSize)
          if (noMore) {
            finish()
            return
          }
          step(page + 1, size)
        }).catch(() => {
          if (this.feedSeq !== seq) return
          if (page === 1 && size && !rows.length) {
            step(1, 0)
            return
          }
          finish()
        })
      }
      step(1, FEED_LIMIT)
    },
    selectOrder(order) {
      this.currentOrder = order
      this.detailLoading = true
      loadOrderDetail(this.activeType, order).then(d => {
        this.detail = d || emptyDetail()
        this.detailLoading = false
      }).catch(() => {
        this.detailLoading = false
      })
    }
  }
}

const FEED_LIMIT = 100

const DASHBOARD_SERVICE_ID = {
  foster: '12',
  tk: '15',
  sn: '18',
  tf: '19',
  boarding: '20'
}

const SUMMARY_ORDER_FIELDS = [
  { id: 'sn', key: 'sn_order_num' },
  { id: 'tf', key: 'tf_order_num' },
  { id: 'tk', key: 'te_kun_order_num' },
  { id: 'boarding', key: 'cri_order_num' },
  { id: 'foster', key: 'foster_order_num' },
  { id: 'hospital', key: 'hospital_order_num' }
]

function toNum(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function matchService(name) {
  const n = String(name || '').replace(/\s/g, '')
  if (!n) return null
  const exact = SERVICE_TYPES.find(s => s.name === n || s.short === n || (s.aliases || []).indexOf(n) > -1)
  if (exact) return exact
  const hits = SERVICE_TYPES.filter(s => {
    if (s.id === 'nursing') return false
    return n.indexOf(s.name) > -1 || s.name.indexOf(n) > -1 || n.indexOf(s.short) > -1
  })
  return hits.length === 1 ? hits[0] : null
}

function emptyDashboard() {
  return {
    cri_order_num: 0,
    foster_order_num: 0,
    hospital_order_num: 0,
    sn_order_num: 0,
    te_kun_order_num: 0,
    tf_order_num: 0,
    total_record_num: 0,
    total_served_user_num: 0,
    year_record_num: 0,
    year_served_user_num: 0,
    record_list: []
  }
}

function colorizePie(rows, colors) {
  return (rows || []).map((row, i) => ({
    name: row.name,
    value: row.value,
    itemStyle: { color: colors[i % colors.length] }
  }))
}

function unpackRecordPage(res) {
  const data = (res && res.data && typeof res.data === 'object') ? res.data : {}
  let list = []
  if (Array.isArray(data.list)) list = data.list
  else if (data.list && Array.isArray(data.list.data)) list = data.list.data
  else if (data.list && Array.isArray(data.list.list)) list = data.list.list
  else if (Array.isArray(data.data)) list = data.data
  else if (Array.isArray(data.records)) list = data.records
  const nested = data.list && typeof data.list === 'object' ? data.list : {}
  return {
    list,
    total: toNum(data.total != null ? data.total : nested.total),
    pageSize: toNum(data.page_size != null ? data.page_size : (data.per_page != null ? data.per_page : nested.per_page))
  }
}

function pushRecords(rows, seen, list) {
  const items = list || []
  items.forEach(row => {
    if (!row) return
    if (row.id != null && row.id !== '') {
      const key = String(row.id)
      if (seen[key]) return
      seen[key] = true
    }
    rows.push(row)
  })
}

function fosterFeedRow(row) {
  const names = []
  if (row && Array.isArray(row.services)) {
    row.services.forEach(item => {
      const name = item && (item.name || item.title || item.service_name)
      if (name) names.push(String(name))
    })
  }
  if (!names.length && row && row.record_name) {
    String(row.record_name).split(/[,，、]/).forEach(name => {
      const text = name.trim()
      if (text) names.push(text)
    })
  }
  return {
    id: row && (row.record_id != null && row.record_id !== '' ? row.record_id : row.id),
    name: (row && (row.user_name || row.name)) || '',
    dis_start_at: row && row.dis_start_at,
    order_status: row && row.order_status,
    is_serviceing: row && row.is_serviceing,
    nurse_name: row && row.nurse_name,
    address: row && row.address,
    town: row && row.town,
    service_names: names
  }
}

function feedFromRecord(row, serviceId) {
  const serving = Number(row && row.is_serviceing) === 1
  const names = Array.isArray(row && row.service_names) ? row.service_names.filter(Boolean) : []
  const item = names.length ? names.join('、') : ((row && row.service_item) || '服务记录')
  const sub = []
  if (row && row.nurse_name) sub.push(row.nurse_name)
  const town = row && row.town ? String(row.town) : ''
  const address = row && row.address ? String(row.address) : ''
  if (town && address.indexOf(town) === -1) sub.push(town)
  if (address) sub.push(address)
  else if (town) sub.push(town)
  const time = String(serviceId) === '12' ? (row && row.dis_start_at) : (row && row.created_at)
  return {
    id: row && row.id,
    t: formatFeedTime(time),
    status: serving ? '服务中' : statusText(row && row.order_status),
    tone: serving ? 'run' : statusTone(row && row.order_status),
    text: `${(row && row.name) || '被服务人'} · ${item}`,
    sub: sub.join(' · ')
  }
}

function formatFeedTime(v) {
  if (v == null || v === '') return '--:--'
  const n = Number(v)
  const d = n && !isNaN(n) ? new Date(n > 1e12 ? n : n * 1000) : new Date(v)
  if (isNaN(d.getTime())) return String(v).slice(-8)
  const p = x => String(x).padStart(2, '0')
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function emptyDetail() {
  return {
    user: { name: '', tel: '', address: '', extra: '' },
    nurse: { name: '', tel: '' },
    status: '',
    tone: '',
    no: '',
    monthNum: 0,
    records: [],
    allItems: [],
    photos: [],
    stats: null
  }
}
</script>

<style lang="scss" scoped>
.screen-page {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #020910;
}
.hud-fx {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    linear-gradient(rgba(46, 230, 214, 0.012) 1px, transparent 1px),
    linear-gradient(90deg, rgba(46, 230, 214, 0.012) 1px, transparent 1px);
  background-size: 60px 60px;
  box-shadow: inset 0 0 80px rgba(0, 0, 0, 0.18);
}
.hud-fx::after {
  content: '';
  position: absolute;
  left: 0; right: 0; height: 120px;
  background: linear-gradient(180deg, transparent, rgba(46, 230, 214, 0.05), transparent);
  animation: scan-move 5s linear infinite;
}
.screen-stage {
  position: absolute;
  z-index: 2;
  width: 1920px;
  height: 1080px;
  transform-origin: top left;
  color: #c8e8f2;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, rgba(4, 16, 28, 0.78) 0 118px, transparent 168px);
  pointer-events: none;
}
.screen-stage.map-pass .hud-hd,
.screen-stage.map-pass .svc-tabs,
.screen-stage.map-pass .hud-left,
.screen-stage.map-pass .hud-right,
.screen-stage.map-pass .overview-center,
.screen-stage.map-pass .detail-center,
.screen-stage.map-pass .home-kpis,
.screen-stage.map-pass .home-col,
.screen-stage.map-pass .home-foot {
  pointer-events: auto;
}

.hud-hd {
  height: 78px;
  display: grid;
  grid-template-columns: 360px 1fr 520px;
  align-items: center;
  padding: 0 18px;
  border-bottom: 1px solid rgba(0, 220, 230, 0.35);
  background:
    linear-gradient(180deg, rgba(0, 28, 48, 0.92), rgba(0, 20, 36, 0.55));
  position: relative;
}
.hud-hd::before,
.hud-hd::after {
  content: '';
  position: absolute;
  bottom: -1px;
  width: 28%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #2ee6d6, transparent);
}
.hud-hd::before { left: 0; }
.hud-hd::after { right: 0; }
.hd-left { display: flex; flex-direction: column; font-size: 13px; color: #7ec8d4; }
.hd-time {
  color: #2ee6d6;
  font-size: 18px;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(46, 230, 214, 0.6);
}
.hd-center {
  display: flex; align-items: center; justify-content: center;
  h1 {
    margin: 0 14px;
    font-size: 30px;
    letter-spacing: 6px;
    color: #e8fbff;
    text-shadow: 0 0 18px rgba(46, 230, 214, 0.55);
    cursor: pointer;
  }
}
.wing {
  width: 110px; height: 10px;
  background:
    linear-gradient(90deg, transparent, #2ee6d6);
  clip-path: polygon(0 50%, 78% 20%, 100% 50%, 78% 80%);
  opacity: 0.85;
  &.right {
    background: linear-gradient(90deg, #2ee6d6, transparent);
    clip-path: polygon(0 50%, 22% 20%, 100% 50%, 22% 80%);
  }
}
.hd-right { display: flex; justify-content: flex-end; gap: 8px; }

.svc-tabs {
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding: 10px 16px 0;
}
.svc-tab {
  flex: 1;
  min-width: 0;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  border: 1px solid rgba(46, 230, 214, 0.28);
  background: rgba(6, 28, 46, 0.78);
  color: #9fd0d8;
  font-size: 13px;
  letter-spacing: 0.5px;
  clip-path: polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
  position: relative;
  .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; box-shadow: 0 0 8px currentColor; }
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  b { color: #2ee6d6; font-size: 12px; flex-shrink: 0; }
  &.home-tab { flex: 0.72; }
  &.on, &:hover {
    color: #e8fbff;
    border-color: #2ee6d6;
    background: linear-gradient(180deg, rgba(0, 90, 100, 0.55), rgba(6, 28, 46, 0.88));
    box-shadow: 0 0 16px rgba(46, 230, 214, 0.2);
  }
  &.on::after {
    content: '';
    position: absolute;
    left: 16px; right: 16px; bottom: 0;
    height: 2px;
    background: #2ee6d6;
    box-shadow: 0 0 8px #2ee6d6;
  }
}
.mode-btn, .ghost-btn {
  height: 28px; padding: 0 12px;
  border: 1px solid rgba(46, 230, 214, 0.45);
  background: rgba(6, 30, 48, 0.75);
  color: #bfeaf0;
  cursor: pointer;
  font-size: 12px;
  clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%);
}
.mode-btn.on, .mode-btn:hover, .ghost-btn:hover {
  background: rgba(46, 230, 214, 0.28);
  color: #fff;
  box-shadow: 0 0 12px rgba(46, 230, 214, 0.35);
}

.hud-main {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 300px 1fr 300px;
  gap: 12px;
  padding: 12px 16px 16px;
  pointer-events: none;
  &.is-home { grid-template-columns: 1fr; }
  &.is-detail { grid-template-columns: 320px 1fr 380px; }
}

.hud-left, .hud-right {
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: rgba(6, 28, 46, 0.82);
  border: 1px solid rgba(46, 230, 214, 0.38);
  box-shadow: 0 0 24px rgba(0, 60, 80, 0.25) inset, 0 0 16px rgba(46, 230, 214, 0.08);
  backdrop-filter: blur(6px);
}
.tech-frame { position: relative; }
.tech-frame::before, .tech-frame::after {
  content: '';
  position: absolute;
  width: 14px; height: 14px;
  border-color: #2ee6d6;
  border-style: solid;
  z-index: 3;
  pointer-events: none;
}
.tech-frame::before { top: -1px; left: -1px; border-width: 2px 0 0 2px; }
.tech-frame::after { top: -1px; right: -1px; border-width: 2px 2px 0 0; }
.pane-title {
  height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  color: #2ee6d6;
  font-weight: 700;
  letter-spacing: 1px;
  border-bottom: 1px solid rgba(46, 230, 214, 0.22);
  background: linear-gradient(90deg, rgba(0, 90, 100, 0.4), transparent);
  .live { margin-left: auto; }
}
.close-x {
  margin-left: auto;
  border: 0; background: transparent; color: #8fd8e0; font-size: 13px; cursor: pointer;
  letter-spacing: 1px;
}
.filter-row { padding: 10px 12px 6px; }
.hud-input {
  width: 100%;
  height: 32px;
  border: 1px solid rgba(46, 230, 214, 0.35);
  background: rgba(2, 16, 28, 0.85);
  color: #d6f4f8;
  padding: 0 10px;
  outline: none;
  box-sizing: border-box;
}

.svc-list {
  list-style: none; margin: 0; padding: 6px 8px 12px; overflow: auto; flex: 1;
  li {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 10px; margin-bottom: 4px; cursor: pointer;
    color: #c5e6ee; font-size: 13px;
    border: 1px solid transparent;
    clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 0 100%);
    &:hover, &.on {
      border-color: rgba(46, 230, 214, 0.5);
      background: rgba(46, 230, 214, 0.1);
      color: #fff;
      box-shadow: inset 0 0 12px rgba(46, 230, 214, 0.12);
    }
  }
  .dot { width: 8px; height: 8px; border-radius: 50%; box-shadow: 0 0 8px currentColor; }
  .svc-name { flex: 1; }
  b { color: #2ee6d6; }
}

.type-brief {
  padding: 10px 12px 4px; font-size: 12px; color: #8fcad4; line-height: 1.6;
}
.tag-row {
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;
  span {
    padding: 1px 8px; font-size: 11px;
    border: 1px solid rgba(46, 230, 214, 0.3); color: #8fe8df;
  }
}
.order-pane { display: flex; flex-direction: column; min-height: 0; flex: 1; }
.order-head { padding: 8px 12px; font-size: 12px; color: #7ec8d4; }
.order-list {
  list-style: none; margin: 0; padding: 0 8px 12px; overflow: auto; flex: 1;
  li {
    padding: 8px 10px; margin-bottom: 6px; cursor: pointer;
    border: 1px solid rgba(46, 230, 214, 0.12);
    background: rgba(4, 22, 36, 0.6);
    &.on, &:hover { border-color: #2ee6d6; background: rgba(0, 70, 80, 0.35); }
  }
  .od-top { display: flex; justify-content: space-between; }
  strong { color: #e8fbff; font-size: 14px; }
  em { font-style: normal; font-size: 11px; }
  .od-sub { margin-top: 3px; font-size: 11px; color: #7aa8b4; }
  .empty { text-align: center; color: #6a9098; border: 0; }
}
em.run { color: #2ee6d6; }
em.done, b.done { color: #7ee08a; }
em.wait { color: #f0c36a; }
em.off { color: #8899a4; }

.hud-center { min-width: 0; min-height: 0; position: relative; }
.hud-center.ghost { pointer-events: none; }
.hud-center.ghost .map-dock { pointer-events: auto; }
.map-window {
  height: 100%;
  position: relative;
}
.map-kpis {
  position: absolute;
  left: 18px;
  right: 18px;
  top: 42px;
  display: flex;
  gap: 10px;
  pointer-events: none;
}
.map-kpi {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  background: rgba(4, 22, 38, 0.72);
  border: 1px solid rgba(46, 230, 214, 0.4);
  box-shadow: 0 0 18px rgba(0, 180, 200, 0.18), inset 0 0 16px rgba(46, 230, 214, 0.06);
  backdrop-filter: blur(8px);
  em { display: block; font-style: normal; font-size: 11px; color: #7ec8d4; letter-spacing: 1px; }
  b { display: block; margin-top: 2px; font-size: 22px; color: #e8fbff; text-shadow: 0 0 10px rgba(46, 230, 214, 0.45); }
  small { font-size: 12px; margin-left: 2px; color: #7ec8d4; font-weight: 500; }
}
.map-ticker {
  position: absolute;
  left: 18px; right: 18px; bottom: 268px;
  display: flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  background: rgba(4, 22, 38, 0.7);
  border: 1px solid rgba(46, 230, 214, 0.28);
  color: #c8f6f0; font-size: 12px; letter-spacing: 1px;
  pointer-events: none;
}
.map-dock {
  position: absolute;
  left: 12px; right: 12px; bottom: 10px;
  height: 248px;
  display: grid;
  grid-template-columns: 1fr 1.2fr 1fr;
  gap: 10px;
  pointer-events: auto;
}
.dock-cell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 6px 8px 4px;
  background: rgba(4, 22, 38, 0.82);
  border: 1px solid rgba(46, 230, 214, 0.32);
  backdrop-filter: blur(8px);
  .stat-hd { margin: 0 0 2px; }
  ::v-deep .screen-chart { flex: 1; min-height: 0; height: auto; }
}
.pulse-dot {
  display: inline-block;
  width: 8px; height: 8px; border-radius: 50%;
  background: #2ee6d6;
  box-shadow: 0 0 8px #2ee6d6;
  animation: pulse 1.4s ease-out infinite;
}
.live {
  display: flex; align-items: center; gap: 6px;
  font-style: normal; font-size: 12px; color: #7ee08a; font-weight: 500;
  &.mini { font-size: 11px; }
}

.overview-center {
  height: 100%;
  display: flex; flex-direction: column;
  pointer-events: auto;
  padding: 0;
}
.ov-hd {
  height: 40px; display: flex; align-items: center; justify-content: space-between;
  padding: 0 12px;
  color: #2ee6d6; border: 1px solid rgba(46, 230, 214, 0.32);
  background: rgba(6, 24, 40, 0.88);
}
.ov-kpis {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; padding: 8px 0;
  &.dense { grid-template-columns: repeat(8, 1fr); padding: 8px 0; }
}
.ov-kpi {
  padding: 8px 10px;
  border: 1px solid rgba(46, 230, 214, 0.28);
  background: linear-gradient(180deg, rgba(0, 70, 80, 0.35), rgba(6, 24, 40, 0.7));
  em { display: block; font-style: normal; font-size: 11px; color: #7ec8d4; }
  b { display: block; margin: 2px 0 0; font-size: 22px; color: #e8fbff; text-shadow: 0 0 10px rgba(46, 230, 214, 0.35); }
  small { font-size: 12px; color: #7ec8d4; margin-left: 2px; }
  span { display: none; }
}
.ov-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 10px;
}
.ov-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 6px 8px;
  border: 1px solid rgba(46, 230, 214, 0.22);
  background: rgba(4, 18, 32, 0.82);
  .stat-hd { margin: 0 0 2px; }
  ::v-deep .screen-chart { flex: 1; min-height: 0; height: auto; }
}
.area-panel .stat-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  em {
    font-style: normal;
    font-size: 11px;
    font-weight: 500;
    color: #7ec8d4;
  }
}
.area-list {
  flex: 1;
  min-height: 0;
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: minmax(0, 1fr);
  column-gap: 12px;
  li {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr) 28px;
    align-items: center;
    gap: 4px;
    min-height: 0;
  }
}
.area-name {
  color: #e8fbff;
  font-size: 12px;
  white-space: nowrap;
}
.area-track {
  display: block;
  height: 4px;
  border-radius: 2px;
  background: rgba(46, 230, 214, 0.12);
  overflow: hidden;
  b {
    display: block;
    height: 100%;
    border-radius: 2px;
    background: linear-gradient(90deg, #1a8f96, #2ee6d6);
  }
}
.area-num {
  font-style: normal;
  text-align: right;
  color: #2ee6d6;
  font-size: 12px;
  font-weight: 700;
}
.area-empty {
  grid-column: 1 / -1;
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #6fa3ae;
  font-size: 12px;
}
.overview-side .feed-viewport {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  mask-image: linear-gradient(180deg, transparent 0, #000 12px, #000 calc(100% - 16px), transparent);
}
.overview-side .feed-viewport:hover .feed-track.rolling {
  animation-play-state: paused;
}
.feed-track {
  &.rolling {
    animation-name: feed-roll;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  }
}
@keyframes feed-roll {
  from { transform: translateY(0); }
  to { transform: translateY(-50%); }
}
.feed-list {
  list-style: none; margin: 0; padding: 4px 12px 8px;
  li {
    display: flex; gap: 8px; padding: 8px 0;
    border-bottom: 1px dashed rgba(46, 230, 214, 0.12);
    i {
      width: 6px; height: 6px; margin-top: 7px; flex: none;
      border-radius: 50%; background: #2ee6d6; box-shadow: 0 0 8px #2ee6d6;
      &.run { background: #2ee6d6; box-shadow: 0 0 8px #2ee6d6; }
      &.wait { background: #f0c36a; box-shadow: 0 0 8px #f0c36a; }
      &.done { background: #7ee08a; box-shadow: 0 0 8px #7ee08a; }
      &.off { background: #8899a4; box-shadow: none; }
    }
    b { display: block; font-size: 11px; color: #2ee6d6; }
    span { display: block; font-size: 12px; color: #cfe8ee; line-height: 1.4; }
    em { display: block; font-style: normal; font-size: 11px; color: #6fa3ae; margin-top: 2px; }
  }
}
.map-through {
  flex: 1;
  min-height: 0;
  position: relative;
}
.ov-chart-wrap { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.ov-chart { flex: 1; min-height: 180px; }
.ov-highlights {
  display: flex; flex-wrap: wrap; gap: 8px; padding-top: 8px;
  span {
    padding: 4px 10px; font-size: 12px; color: #c8f6f0;
    border: 1px solid rgba(46, 230, 214, 0.35);
    background: rgba(0, 80, 90, 0.25);
  }
}
.ov-side {
  flex: 1; min-height: 0; overflow: auto; padding: 8px 10px 12px;
}
.gauge-row {
  display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
  flex: 1; min-height: 0;
}
.gauge-box {
  height: auto; min-height: 0; position: relative;
  display: flex; flex-direction: column;
  ::v-deep .screen-chart { flex: 1; min-height: 0; }
  p {
    position: absolute; left: 0; right: 0; bottom: 4px;
    text-align: center; margin: 0; font-size: 12px; color: #7ec8d4; letter-spacing: 1px;
  }
}
.bar-box { height: 150px; &.sm { height: 132px; } }
.mw-corner {
  position: absolute; width: 18px; height: 18px; border-color: #2ee6d6; border-style: solid;
  &.lt { top: 0; left: 0; border-width: 2px 0 0 2px; }
  &.rt { top: 0; right: 0; border-width: 2px 2px 0 0; }
  &.lb { bottom: 0; left: 0; border-width: 0 0 2px 2px; }
  &.rb { bottom: 0; right: 0; border-width: 0 2px 2px 0; }
}
.mw-tag {
  position: absolute;
  right: 16px; top: 10px;
  font-size: 12px;
  letter-spacing: 2px;
  color: rgba(46, 230, 214, 0.75);
  text-shadow: 0 0 8px #041018;
}

.board-list {
  height: 100%; overflow: auto;
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  pointer-events: auto;
}
.board-card {
  padding: 16px; cursor: pointer;
  background: rgba(6, 28, 46, 0.86);
  border: 1px solid rgba(46, 230, 214, 0.28);
  &:hover { border-color: #2ee6d6; box-shadow: 0 0 16px rgba(46, 230, 214, 0.15); }
  p { margin: 8px 0; font-size: 12px; color: #8fcad4; line-height: 1.6; }
}
.bc-hd {
  display: flex; align-items: center; gap: 8px; color: #e8fbff;
  .dot { width: 8px; height: 8px; border-radius: 50%; }
  span { margin-left: auto; color: #2ee6d6; font-size: 13px; }
}

.detail-center {
  height: 100%;
  display: flex; flex-direction: column;
  border: 1px solid rgba(46, 230, 214, 0.32);
  background: rgba(6, 24, 40, 0.88);
  pointer-events: auto;
}
.dc-hd {
  height: 42px; display: flex; align-items: center; justify-content: space-between;
  padding: 0 12px; color: #2ee6d6; border-bottom: 1px solid rgba(46, 230, 214, 0.18);
}
.dc-modes button {
  height: 24px; margin-left: 6px; padding: 0 10px; cursor: pointer;
  border: 1px solid rgba(46, 230, 214, 0.35); background: transparent; color: #9fd0d8;
  &.on { background: rgba(46, 230, 214, 0.2); color: #fff; }
}
.dc-empty {
  flex: 1; display: flex; align-items: center; justify-content: center;
  color: #6a9098; font-size: 14px;
  &.mini { min-height: 120px; padding: 20px; text-align: center; }
}
.photo-grid {
  flex: 1; min-height: 0;
  display: grid; grid-template-columns: 1fr 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 8px; padding: 8px;
  &.single { grid-template-columns: 1fr; grid-template-rows: 1fr; }
}
.photo-cell {
  position: relative; overflow: hidden; background: #071828; border: 1px solid rgba(46, 230, 214, 0.16);
  ::v-deep .el-image { width: 100%; height: 100%; }
  em {
    position: absolute; left: 8px; bottom: 6px; padding: 1px 8px;
    font-style: normal; font-size: 12px; color: #e8fbff;
    background: rgba(0, 20, 32, 0.65);
  }
}
.photo-placeholder {
  height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #5e8894; gap: 8px; font-size: 13px;
  i { font-size: 28px; color: #2ee6d6; opacity: 0.5; }
}
.photo-single {
  flex: 1; padding: 8px;
  ::v-deep .el-image { width: 100%; height: 100%; }
}

.acc { flex: 1; overflow: auto; padding: 8px 10px 12px; }
.acc-block { margin-bottom: 10px; }
.acc-hd {
  color: #2ee6d6; font-size: 13px; font-weight: 700; margin-bottom: 8px;
  padding-left: 8px; border-left: 3px solid #2ee6d6;
}
.kv {
  list-style: none; margin: 0; padding: 0;
  li { display: flex; gap: 8px; padding: 4px 0; font-size: 12px; color: #9fd0d8; }
  span { width: 64px; flex: none; color: #6fa3ae; }
  b { font-weight: 600; color: #e8fbff; }
}
.person-info {
  flex: 1; min-height: 0; overflow: auto; padding: 10px 12px 12px;
}
.quick-row {
  display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 12px;
  span {
    padding: 4px 10px; font-size: 12px; cursor: default;
    border: 1px solid rgba(46, 230, 214, 0.4); color: #c8f6f0;
    background: rgba(0, 70, 80, 0.35);
  }
}
.stat-hd {
  color: #2ee6d6; font-size: 13px; font-weight: 700; margin: 8px 0 4px;
  padding-left: 8px; border-left: 3px solid #2ee6d6;
}
.mini-chart {
  height: 150px;
  border: 1px solid rgba(46, 230, 214, 0.16);
  background: rgba(4, 18, 32, 0.45);
  margin-bottom: 8px;
}
.pick-hint {
  height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #6a9098; letter-spacing: 2px;
  border: 1px dashed rgba(46, 230, 214, 0.25);
}
.rec-note { font-size: 12px; color: #9fd0d8; margin-top: 4px; line-height: 1.5; }
.check-list {
  list-style: none; margin: 0; padding: 0;
  li {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 0; font-size: 13px; color: #d6f0f4;
    border-bottom: 1px dashed rgba(46, 230, 214, 0.12);
    i { color: #7ee08a; }
    em { margin-left: auto; font-style: normal; font-size: 11px; color: #7ee08a; }
  }
}
.rec-card {
  padding: 8px; margin-bottom: 8px;
  border: 1px solid rgba(46, 230, 214, 0.16);
  background: rgba(4, 20, 32, 0.5);
}
.rec-top { display: flex; gap: 8px; }
.rec-top em {
  width: 18px; height: 18px; flex: none;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; background: #16525a; color: #2ee6d6; font-style: normal; font-size: 11px;
}
.rec-time { font-size: 12px; color: #8fe8df; }
.rec-items { font-size: 12px; color: #cfe8ee; margin-top: 2px; line-height: 1.5; }
.rec-imgs {
  display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap;
  ::v-deep .el-image { width: 64px; height: 48px; border: 1px solid rgba(46, 230, 214, 0.2); }
}
.empty { color: #6a9098; font-size: 12px; padding: 8px 0; }

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(1.8); opacity: 0; }
}
@keyframes scan-move {
  0% { top: -120px; }
  100% { top: 100%; }
}
</style>
